import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Switch, Empty } from 'antd';
import {
  createTableChangeHandler,
  createScrollSync,
  createPaginationConfig,
} from '@client/utils/table/tableUtils';
import {
  createTableColumns,
  createExpandedRowRender,
} from '@client/feature/activeSubstance/config/tableConfig';

const ResultItems = ({ data, onPageChange, onSortChange, currentSorting }) => {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const tableWrapperRef = useRef(null);
  const tableHeaderRef = useRef(null);

  const results = data?.results || [];
  const pageSize = data?.size || 20;

  const filteredResults = useMemo(
    () =>
      showSelectedOnly
        ? results.filter((item) => selectedRowKeys.includes(item.id))
        : results,
    [showSelectedOnly, results, selectedRowKeys]
  );

  const columns = useMemo(
    () => createTableColumns(t, currentSorting),
    [t, currentSorting]
  );

  const handleTableChange = useMemo(
    () => createTableChangeHandler(onPageChange, onSortChange, columns),
    [onPageChange, onSortChange, columns]
  );

  const expandedRowRender = useMemo(() => createExpandedRowRender(), []);

  useEffect(() => {
    const tableHeader = tableHeaderRef.current;
    const tableBody = document.querySelector('.ant-table-content');
    return createScrollSync(tableHeader, tableBody);
  }, [data?.page, filteredResults]);

  useEffect(() => {
    const currentPageKeys = filteredResults.map((item) => item.id);
    setExpandedRowKeys(currentPageKeys);
  }, [filteredResults]);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: setSelectedRowKeys,
    }),
    [selectedRowKeys]
  );

  const pagination = useMemo(
    () => createPaginationConfig(data, pageSize, t),
    [data, pageSize, t]
  );

  if (!results.length) {
    return (
      <div className="text-center py-8">
        <Empty
          description={t('active-substance|No results found')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[1300px]">
        <div ref={tableHeaderRef} className="header-wrapper">
          <div className="table-header flex justify-between border-b border-gray-300 pb-4">
            <div className="flex items-center gap-2">
              <Switch
                checked={showSelectedOnly}
                onChange={setShowSelectedOnly}
                disabled={selectedRowKeys.length === 0}
              />
              <span>{t('common|Show only my selections')}</span>
            </div>
            <div className="flex flex-col">
              <div className="w-[600px] flex justify-between border-t border-gray-300">
                <div className="min-w-[140px] text-xs font-bold p-2.5 text-center h-11 flex items-center justify-center border-r border-gray-300">
                  {t('active-substance|table.Risks on health')}
                </div>
                <div className="flex-1 text-xs font-bold p-2.5 text-center flex items-center justify-center">
                  {t('active-substance|table.Risks on environment')}
                </div>
              </div>
              <div className="w-[600px] flex justify-between border-t border-gray-300">
                <div className="max-w-[140px] text-xs font-bold p-2.5 text-center border-r border-gray-300">
                  {t('active-substance|table.Mammals (including humans)')}
                </div>
                <div className="flex-1 text-xs font-bold p-2.5 text-center flex items-center justify-center border-r border-gray-300">
                  {t('active-substance|table.Non target species')}
                </div>
                <div className="flex-1 text-xs font-bold p-2.5 text-center flex items-center justify-center">
                  {t('active-substance|table.Fate and behaviour')}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={tableWrapperRef}>
          <Table
            className="custom-table"
            columns={columns}
            dataSource={filteredResults}
            rowKey="id"
            rowClassName="custom-row"
            rowSelection={rowSelection}
            pagination={pagination}
            onChange={handleTableChange}
            loading={!data}
            hideOnSinglePage
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpand: (expanded, record) => {
                setExpandedRowKeys((prev) =>
                  expanded
                    ? [...prev, record.id]
                    : prev.filter((key) => key !== record.id)
                );
              },
              expandIcon: () => null,
              expandIconColumnIndex: -1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ResultItems;
